import { useEffect, useRef } from 'react'

/**
 * Global bare-key shortcut layer (Track P). Vim-style, no modifier:
 *   /          open search (command palette)
 *   n          new / quick capture
 *   ?          shortcuts help overlay
 *   g then t   go to Today       g then i   go to Inbox
 *   g then p   go to Pipeline    g then c   go to Contacts
 *
 * Modifier combos (⌘K, ⌘N, ⌘J) stay owned by AppLayout — this layer ignores any
 * event with a meta/ctrl/alt key so the two never fight. It also ignores keys
 * typed into inputs, textareas, selects, or contenteditable regions.
 */

export interface ShortcutHandlers {
  onSearch: () => void
  onNew: () => void
  onHelp: () => void
  onNavigate: (path: string) => void
}

// `g` then this key → route. Covers the consolidated top-level surfaces.
export const GOTO_ROUTES: Record<string, string> = {
  t: '/today',
  i: '/inbox',
  p: '/pipeline',
  c: '/contacts',
  k: '/tasks',
  l: '/calendar',
  d: '/delivery',
  r: '/reports',
  f: '/finance',
}

function isTypingTarget(el: EventTarget | null): boolean {
  if (!(el instanceof HTMLElement)) return false
  const tag = el.tagName
  return (
    tag === 'INPUT' ||
    tag === 'TEXTAREA' ||
    tag === 'SELECT' ||
    el.isContentEditable ||
    el.getAttribute('role') === 'textbox'
  )
}

export function useKeyboardShortcuts(handlers: ShortcutHandlers): void {
  // Keep the latest handlers without re-binding the listener each render.
  const ref = useRef(handlers)
  ref.current = handlers

  useEffect(() => {
    let gPending = false
    let gTimer: ReturnType<typeof setTimeout> | undefined

    const clearG = () => {
      gPending = false
      if (gTimer) clearTimeout(gTimer)
    }

    const onKeyDown = (e: KeyboardEvent) => {
      if (e.metaKey || e.ctrlKey || e.altKey) return
      if (isTypingTarget(e.target)) return

      // Second key of a `g` chord.
      if (gPending) {
        const route = GOTO_ROUTES[e.key.toLowerCase()]
        clearG()
        if (route) {
          e.preventDefault()
          ref.current.onNavigate(route)
        }
        return
      }

      switch (e.key) {
        case '/':
          e.preventDefault()
          ref.current.onSearch()
          break
        case 'n':
        case 'N':
          e.preventDefault()
          ref.current.onNew()
          break
        case '?':
          e.preventDefault()
          ref.current.onHelp()
          break
        case 'g':
        case 'G':
          gPending = true
          gTimer = setTimeout(clearG, 1200)
          break
        default:
          break
      }
    }

    document.addEventListener('keydown', onKeyDown)
    return () => {
      document.removeEventListener('keydown', onKeyDown)
      clearG()
    }
  }, [])
}
